// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: cbfeb44a-9ef8-4b22-a766-5581e58e455e

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Alert, FormControlLabel, Checkbox, CircularProgress, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import KeyIcon from '@mui/icons-material/Key';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getConfig } from './config';
import { getIdToken, getSession } from './auth';

// User administration. Backed by Cognito through the API — the server re-checks
// every permission, so this UI is convenience, not the security boundary.
//   master (OptimumAdmins) -> manage users in any tenant
//   tenant admin           -> manage users in their own tenant only

const ROLES = ['admin', 'editor', 'viewer'];
const BLANK = {
  email: '', firstName: '', lastName: '', tenantId: '', tenantName: '',
  roleId: 'viewer', isMaster: false, domainOverride: false, temporaryPassword: '',
};

export default function UserAdmin({ tenants = [] }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [dialog, setDialog] = useState(null); // { mode:'create'|'edit', user }
  const [busy, setBusy] = useState(false);

  const me = getSession();
  const iAmMaster = !!me && (me.groups || []).includes('OptimumAdmins');

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    // An error envelope is often an HTML page, not JSON. Parsing it bare threw
    // SyntaxError here, before the !res.ok check below could report the real
    // status, so a 502 surfaced to the user as "Unexpected token <".
    let data = {};
    let parseFailed = false;
    try { data = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
    if (!res.ok) throw new Error(data.error || data.message || `request failed (${res.status})`);
    if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
    return data;
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    // Any error envelope that is not an array used to reach users.map and
    // throw during render — a white screen instead of a message.
    try {
      const list = await api('/users');
      if (!Array.isArray(list)) throw new Error('The user service returned an unexpected response.');
      setUsers(list);
    }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const u = dialog.user;
    setBusy(true); setErr(''); setMsg('');
    try {
      const tenant = tenants.find((t) => t.id === u.tenantId);
      const payload = { ...u, tenantName: tenant ? tenant.name : u.tenantName };
      if (dialog.mode === 'create') {
        await api('/users', { method: 'POST', body: JSON.stringify(payload) });
        setMsg(`Created ${u.email}. Temporary password: ${u.temporaryPassword}`);
      } else {
        await api('/users', { method: 'PUT', body: JSON.stringify(payload) });
        setMsg(`Updated ${u.email}${u.resetPassword ? ` — temporary password: ${u.resetPassword}` : ''}`);
      }
      setDialog(null);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? They will lose access immediately.`)) return;
    setErr(''); setMsg('');
    try {
      await api(`/users?email=${encodeURIComponent(u.email)}&tenantId=${encodeURIComponent(u.tenantId)}`, { method: 'DELETE' });
      setMsg(`Deleted ${u.email}`);
      await load();
    } catch (e) { setErr(e.message); }
  };

  const genPassword = () => `Primordia${Math.floor(1000 + Math.random() * 9000)}!`;
  const setU = (patch) => setDialog((d) => ({ ...d, user: { ...d.user, ...patch } }));

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Users</Typography>
        <Chip size="small" label={iAmMaster ? 'Optimum master — all tenants' : `${me && me.tenantName ? me.tenantName : 'your tenant'} only`} />
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={load} title="Refresh"><RefreshIcon /></IconButton>
        <Button startIcon={<AddIcon />} variant="contained" sx={{ bgcolor: '#0D5B9A', '&:hover': { bgcolor: '#021F4B' } }}
          onClick={() => setDialog({ mode: 'create', user: { ...BLANK, tenantId: iAmMaster ? '' : (me && me.tenantId) || '', temporaryPassword: genPassword() } })}>
          New user
        </Button>
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.username} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{u.email}</TableCell>
                  <TableCell>{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</TableCell>
                  <TableCell>
                    {u.isMaster
                      ? <Chip size="small" label="ALL TENANTS" sx={{ bgcolor: '#021F4B', color: '#fff', height: 20 }} />
                      : (u.tenantName || u.tenantId || '—')}
                  </TableCell>
                  <TableCell><Chip size="small" label={u.roleId} variant="outlined" sx={{ height: 20 }} /></TableCell>
                  <TableCell>
                    <Chip size="small" label={u.status === 'FORCE_CHANGE_PASSWORD' ? 'invite pending' : (u.status || '').toLowerCase()}
                      color={u.status === 'CONFIRMED' ? 'success' : 'default'} sx={{ height: 20 }} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit / assign">
                      <IconButton size="small" onClick={() => setDialog({ mode: 'edit', user: { ...u } })}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete user">
                      <IconButton size="small" onClick={() => remove(u)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!users.length && (
                <TableRow><TableCell colSpan={6} sx={{ color: '#9AA6B2', textAlign: 'center', py: 3 }}>No users yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={!!dialog} onClose={() => !busy && setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: '#021F4B' }}>
          {dialog && dialog.mode === 'create' ? 'Create user' : 'Edit user'}
        </DialogTitle>
        {dialog && (
          <DialogContent dividers>
            <TextField fullWidth size="small" label="Email" sx={{ mb: 2 }} value={dialog.user.email}
              disabled={dialog.mode === 'edit'} onChange={(e) => setU({ email: e.target.value })} />
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField fullWidth size="small" label="First name" value={dialog.user.firstName || ''} onChange={(e) => setU({ firstName: e.target.value })} />
              <TextField fullWidth size="small" label="Last name" value={dialog.user.lastName || ''} onChange={(e) => setU({ lastName: e.target.value })} />
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField select fullWidth size="small" label="Tenant" value={dialog.user.tenantId || ''}
                disabled={!iAmMaster} onChange={(e) => setU({ tenantId: e.target.value })}>
                {tenants.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
              <TextField select fullWidth size="small" label="Role" value={dialog.user.roleId || 'viewer'}
                onChange={(e) => setU({ roleId: e.target.value })}>
                {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            </Stack>

            {iAmMaster && (
              <FormControlLabel sx={{ display: 'block' }}
                control={<Checkbox size="small" checked={!!dialog.user.isMaster} onChange={(e) => setU({ isMaster: e.target.checked })} />}
                label={<span style={{ fontSize: 13 }}>Optimum master — can see and manage <b>all</b> client tenants</span>} />
            )}
            <FormControlLabel sx={{ display: 'block' }}
              control={<Checkbox size="small" checked={!!dialog.user.domainOverride} onChange={(e) => setU({ domainOverride: e.target.checked })} />}
              label={<span style={{ fontSize: 13 }}>Email-domain override (allow an address outside the client's domain)</span>} />

            {dialog.mode === 'create' ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                <TextField fullWidth size="small" label="Temporary password" value={dialog.user.temporaryPassword}
                  onChange={(e) => setU({ temporaryPassword: e.target.value })}
                  helperText="They will be asked to set a permanent password at first sign-in." />
                <Button size="small" startIcon={<KeyIcon />} onClick={() => setU({ temporaryPassword: genPassword() })}>New</Button>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                <TextField fullWidth size="small" label="Reset password (optional)" value={dialog.user.resetPassword || ''}
                  onChange={(e) => setU({ resetPassword: e.target.value })}
                  helperText="Leave blank to keep the current password." />
                <Button size="small" startIcon={<KeyIcon />} onClick={() => setU({ resetPassword: genPassword() })}>New</Button>
              </Stack>
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={busy || !dialog || !dialog.user.email}
            sx={{ bgcolor: '#0D5B9A', '&:hover': { bgcolor: '#021F4B' } }}>
            {busy ? 'Saving…' : (dialog && dialog.mode === 'create' ? 'Create user' : 'Save changes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
